import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  CubeIcon,
  CreditCardIcon,
  UsersIcon,
  ChartBarIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import { useGetDashboardStatsQuery } from '../../store/api/superAdminApi';
import { formatCurrency } from '../../utils/formatters';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import SubscriptionReminders from '../../components/SuperAdmin/SubscriptionReminders';

const SuperAdminDashboard = () => {
  const { data, isLoading, error } = useGetDashboardStatsQuery();

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Failed to load dashboard data" />;

  const stats = data?.data?.stats || {};
  const recentActivities = data?.data?.recentActivities || {};
  const planUsage = data?.data?.planUsage || [];
  const subscriptionStats = data?.data?.subscriptionStats || [];

  const statCards = [
    {
      title: 'Total Companies',
      value: stats.companies?.total || 0,
      active: stats.companies?.active || 0,
      icon: BuildingOfficeIcon,
      color: 'blue',
      trend: '+12%',
      trendDirection: 'up'
    },
    {
      title: 'Active Plans',
      value: stats.plans?.active || 0,
      total: stats.plans?.total || 0,
      icon: CubeIcon,
      color: 'purple',
      trend: '+5%',
      trendDirection: 'up'
    },
    {
      title: 'Subscriptions',
      value: stats.subscriptions?.active || 0,
      total: stats.subscriptions?.total || 0,
      icon: CreditCardIcon,
      color: 'green',
      trend: '+18%',
      trendDirection: 'up'
    },
    {
      title: 'Total Employees',
      value: stats.employees || 0,
      icon: UsersIcon,
      color: 'orange',
      trend: '+25%',
      trendDirection: 'up'
    },
    {
      title: 'Monthly Revenue',
      value: formatCurrency(stats.monthlyRevenue || 0, 'INR'),
      icon: BanknotesIcon,
      color: 'emerald',
      trend: '+22%',
      trendDirection: 'up'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500 text-blue-600 bg-blue-50',
      purple: 'bg-purple-500 text-purple-600 bg-purple-50',
      green: 'bg-green-500 text-green-600 bg-green-50',
      orange: 'bg-orange-500 text-orange-600 bg-orange-50',
      emerald: 'bg-emerald-500 text-emerald-600 bg-emerald-50'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Super Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Monitor and manage your HRMS platform</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const [bgColor, textColor, lightBg] = getColorClasses(stat.color).split(' ');
            const IconComponent = stat.icon;

            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className={`p-3 rounded-lg ${lightBg}`}>
                    <IconComponent className={`w-6 h-6 ${textColor}`} />
                  </div>
                  <div className="flex items-center text-sm">
                    {stat.trendDirection === 'up' ? (
                      <ArrowUpIcon className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <ArrowDownIcon className="w-4 h-4 text-red-500 mr-1" />
                    )}
                    <span className={stat.trendDirection === 'up' ? 'text-green-600' : 'text-red-600'}>
                      {stat.trend}
                    </span>
                  </div>
                </div>
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-600">{stat.title}</h3>
                  <div className="flex items-baseline mt-2">
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    {stat.total && (
                      <span className="ml-2 text-sm text-gray-500">/ {stat.total}</span>
                    )}
                    {stat.active && stat.title === 'Total Companies' && (
                      <span className="ml-2 text-sm text-green-600">({stat.active} active)</span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Subscription Reminders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <SubscriptionReminders />
        </motion.div>

        {/* Charts and Activity Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Subscription Status Chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Subscription Status</h2>
              <ChartBarIcon className="w-6 h-6 text-gray-400" />
            </div>
            <div className="space-y-4">
              {subscriptionStats.map((stat) => {
                const percentage = stats.subscriptions?.total 
                  ? Math.round((stat.count / stats.subscriptions.total) * 100)
                  : 0;
                
                const statusColors = {
                  active: 'bg-green-500',
                  trial: 'bg-blue-500',
                  past_due: 'bg-yellow-500',
                  canceled: 'bg-red-500',
                  expired: 'bg-gray-500'
                };

                return (
                  <div key={stat._id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3 ${statusColors[stat._id] || 'bg-gray-400'}`}></div>
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {stat._id.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-2">{stat.count}</span>
                      <span className="text-xs text-gray-400">({percentage}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Plan Usage */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Popular Plans</h2>
              <ArrowTrendingUpIcon className="w-6 h-6 text-gray-400" />
            </div>
            <div className="space-y-4">
              {planUsage.slice(0, 5).map((plan, index) => (
                <div key={plan._id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white text-sm font-bold">{index + 1}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700">{plan._id}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">{plan.count} users</div>
                    <div className="text-xs text-gray-500">{formatCurrency(plan.revenue, 'INR')}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Recent Activities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Companies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Companies</h2>
            <div className="space-y-4">
              {recentActivities.companies?.slice(0, 5).map((company) => (
                <div key={company._id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{company.name}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(company.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      company.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {company.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Recent Subscriptions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Subscriptions</h2>
            <div className="space-y-4">
              {recentActivities.subscriptions?.slice(0, 5).map((subscription) => (
                <div key={subscription._id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {subscription.companyId?.name || 'Unknown Company'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {subscription.planId?.name} • {formatCurrency(subscription.price, 'INR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      subscription.status === 'active' 
                        ? 'bg-green-100 text-green-800'
                        : subscription.status === 'trial'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {subscription.status}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {subscription.billingCycle}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;